import { useCallback, useContext, useMemo } from 'react';
/**
 * A custom hook that combines the use of a store hook with a memoized selector.
 * It efficiently retrieves and memoizes a specific slice of state from a global state management store
 * (like Redux or Zustand) using a selector function. This hook abstracts away the memoization logic,
 * making it easier to use and ensuring that components only re-render when the selected slice of state changes.
 *
 * Usage:
 * ```
 * const selectedState = useStoreSelector(useMyStore, state => state.partOfState, [deps]);
 * ```
 *
 * @param {Function} storeHook - The store hook used to access the global state.
 * @param {Function} selector - The selector function to retrieve a specific slice of state.
 * @param {Array} deps - Optional. Dependencies array to control the memoization of the selector.
 * @returns {*} The selected state.
 */
export const useStoreSelector = (storeHook, selector, deps = []) => {
    const memoizedSelector = useCallback(selector, deps);
    return storeHook(memoizedSelector);
};

export const useContextSelector = (context, selector, deps = []) => {
    const contextValue = useContext(context);
    if (!contextValue) {
        console.error(
            'Context value is undefined. Ensure your component is wrapped in the appropriate provider.'
        );
        return undefined;
    }
    const selectedValue = useMemo(
        () => selector(contextValue),
        [contextValue, ...deps]
    );
    return selectedValue;
};
